import React, {useState, useEffect} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Input, {AnalysisHeader, Message, MsgFile} from '../Input';
import { useResults } from '../context/ResultsProvider';
import { loading, useSupport, 
  processSupport, setSupportForm, showSupport, 
  resetSupport, getSupportDescription, setSupportFile, setSupportError } from '../context/SupportProvider';
import useApiResult from '../useApiResult';
import { displayName } from '../identity';
import FormLabel from '@proofpoint/fuse/components/FormLabel';
import Button from '@proofpoint/fuse/components/Button';
import Typography from '@proofpoint/fuse/components/Typography';
import TextArea from '@proofpoint/fuse/components/TextArea';
import TextField from '@proofpoint/fuse/components/TextField';
import Grid from '@mui/material/Grid';
import Loading from './Loading';

export const SupportButton = ({setFlash}) => {
  const [results] = useResults();
  const [supportResults, dispatch] = useSupport();
  const [show, setShow] = useState(false);
 
  useEffect(() => {
    setShow(supportResults.show);
  }, [supportResults.show]);

  const setShowSupport = () => {
    setFlash({open:false});
    dispatch(showSupport(true));
  }

  const cancel = (event) => {
    setFlash({open:false});
    dispatch(showSupport(false));
  };

  if (results.loading) {
      return (<Button loading={true}>Open Support Case</Button>);
  }
  if (results.done && !show) {
    return(
      <Button onClick={setShowSupport} aria-label="Open Support Case" icon={<HelpOutlineIcon />}>Open Support Case</Button>
    );
  }
  if (results.done && show) {
    return(
      <Button onClick={cancel} aria-label="Cancel Support Case" icon={<CloseIcon />}>Cancel Support Case</Button>
    );
  }
  return null;
}

const SupportForm = ({setFlash}) => {
  const [results] = useResults();
  const [supportResults, dispatch] = useSupport();
  const { user } = useAuth0();
  const [verified, setVerified] = useState(null);
  
  useApiResult(supportResults.request, dispatch);  


  useEffect(() => {
    const s = supportResults;
    if (s.form.contact_company === "") {
      dispatch(setSupportForm({target: {name:"contact_company", value:displayName(user)}}));
    }
    if (s.done) {
      setFlash({severity:"success", message:"Support request sent", open:true});
      dispatch(resetSupport());
    }
    if (s.error) {
      let error_message;
      try {
        const jr = JSON.parse(s.error);
        error_message  = (Object.prototype.hasOwnProperty.call(jr, 'message')) ? jr.message : JSON.stringify(jr);
      } catch (e) {
        error_message = s.error
      }
      setFlash({severity:"error", message:error_message, open:true});
    }
  }, [supportResults, user, setFlash, dispatch])
 
  const handleChange = (event) => {
    if (event.target.name === "sample_file") {
      const file = event.target.files?.[0];
      if (!file) return;
      const maxFileSize = parseInt(process.env.REACT_APP_MAX_FILESIZE_MB, 10)*(1024**2);
      const input = Input.detectType(file);
      if (input == null) return;
      if (input.file.size > maxFileSize) {
        dispatch(setSupportError("Maximum file size for a message is "+(maxFileSize/(1024**2))+"Mb"));
        return;
      }
      input.readFile().then(buffer => {
        // Detect legacy MS Office files and avoid previewing as text
        if (indexedDB.cmp(new Uint8Array((buffer.slice(0, 8))), new Uint8Array([0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1])) === 0) {
          input.toBase64();
        } else {
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(buffer);
          input.setContent(text);
        }
        dispatch(setSupportFile(input));
      }).catch(err => dispatch(setSupportError(err)));
    } else {
      dispatch(setSupportForm(event));
    }
  };

  const openSupport = () => {
    setFlash({open:false});
    if (verifyForm("supportForm")) {
      dispatch(loading());
      dispatch(processSupport(results));
    }
  }
  
  const verifyForm = (form_name) => {
    let isComplete = true;
    for (const input in supportResults.form) {
      if (document.forms[form_name].elements[input]?.required && supportResults.form[input] === "") isComplete = false;
    }
    setVerified(isComplete);
    return isComplete;
  }

  const hasError = (element) => {
    if (verified === null) return false;
    if (element.match(/email/i) !== null) return (supportResults.form[element].match(/\S+@\S+\.\S+/) === null)
    return (supportResults.form[element] === "");
  }

  if (!supportResults.show) return null;
  
  if (!results.data) return null;
 
  
  return (
  <>
    <Loading show={supportResults.loading}/>
    <Card>
      <CardContent>
        <form id="supportForm" name="supportForm">
          <Grid container spacing={4}>
            <Grid item={true} xs={3}>
              <TextField
                label="Contact Name"
                name="contact_name"
                placeholder="First Last"
                fullWidth={true}
                required={true}
                inputProps={{"maxLength":50}}
                onChange={handleChange}
                error={hasError("contact_name")}
                helperText={hasError("contact_name")?"Required":""}
              />
            </Grid>
            <Grid item={true} xs={3}>
              <TextField
                label="Contact Email"
                name="contact_email"
                placeholder="you@yourcompany.com"
                fullWidth={true}
                required={true}
                inputProps={{"maxLength":100}}
                onChange={handleChange}
                error={hasError("contact_email")}
                helperText={hasError("contact_email")?"A valid email address is required":"We will use this address to communicate about the issue"}
              />
            </Grid>
            <Grid item={true} xs={3}>
              <TextField
                label="Contact Company"
                name="contact_company"
                fullWidth={true}
                required={true}
                inputProps={{"maxLength":50}}
                defaultValue={supportResults.form.contact_company}
                onChange={handleChange}
                error={hasError("contact_company")}
                helperText={hasError("contact_company")?"Required":""}
              />
            </Grid>
            <Grid item={true} xs={3}>
              <TextField
                label="Contact Phone"
                name="contact_phone"
                placeholder="123 456 7890"
                fullWidth={true}
                inputProps={{"maxLength":20}}
                onChange={handleChange}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <TextField
                label="Issue Summary"
                name="issue_summary"
                placeholder="Short issue description"
                fullWidth={true}
                required={true}
                inputProps={{"maxLength":96}}
                onChange={handleChange}
                error={hasError("issue_summary")}
                helperText={hasError("issue_summary")?"Required":""}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <TextArea
                label="Additional Details"
                placeholder="Any additional information that will help us address your issue"
                fullWidth={true}
                name="additional_details"
                rows={3}
                onChange={handleChange}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <TextArea
                label="The following will be sent to Support"
                fullWidth={true}
                name="content"
                value={getSupportDescription(results)}
                rows={6}
                disabled={true} 
              />
            </Grid>
            <Attachment type={results.input} onChange={handleChange}/>
            <Grid item={true} xs={12}>
              <Button onClick={openSupport} aria-label="Submit">Submit</Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  </>);
}

const Attachment = ({type, onChange}) => {
  switch (true) {
    case type instanceof Message:
    case type instanceof MsgFile:
      return (
        <Grid item={true} xs={12} style={{ alignItems: 'center', display: 'inline-flex' }}>
          <AttachFileIcon/> <Typography variant="subtitle2">Submitted email message</Typography>
        </Grid>
      );
    case type instanceof AnalysisHeader:
      return (
        <Grid item={true} xs={12} style={{ alignItems: 'center', display: 'inline-flex' }}>
          <FormLabel>Optional message sample <input id="sample_file" type="file" name="sample_file" onChange={onChange} /></FormLabel>
        </Grid>
      );
    default:
      return null;
  }
}

export default SupportForm;
